// AGC: Price Service - Fetch commodity prices based on GPS location
// AGC: Uses backend API that finds nearest pricing plaza
// AGC: Includes caching for performance (5-minute TTL)

import { getBackendUrl } from '../utils/backendUrl'
import { getCurrentLocation } from './weatherService'

// AGC: Backend URL for API calls
const BASE_URL = getBackendUrl()

// AGC: Cache TTL in milliseconds (5 minutes)
const CACHE_TTL = 5 * 60 * 1000

// AGC: Price cache - now includes location to detect changes
interface PriceCache {
  timestamp: number
  lat: number
  lon: number
  data: CropPrices
  reference: PriceReference | null
}

let priceCache: PriceCache | null = null

// AGC: Helper to check if coordinates are close enough (within ~10km)
const isSameLocation = (lat1: number, lon1: number, lat2: number, lon2: number): boolean => {
  const threshold = 0.1 // AGC: ~10km at equator
  return Math.abs(lat1 - lat2) < threshold && Math.abs(lon1 - lon2) < threshold
}

// =============================================================================
// AGC: Types
// =============================================================================

/**
 * AGC: Price information for a single crop
 */
export interface CropPrice {
  preco: number
  unidade: string // AGC: 'R$/sc' or 'USD/tn'
}

/**
 * AGC: Prices for multiple crops
 */
export interface CropPrices {
  [cropType: string]: CropPrice
}

/**
 * AGC: Reference plaza information
 */
export interface PriceReference {
  cidade: string
  estado: string
  fonte: string
  distancia_km: number
}

/**
 * AGC: Single price lookup response
 */
export interface SinglePriceResponse {
  produto: string
  preco: number
  unidade: string
  cidade: string
  estado: string
  pais: string
  fonte: string
  distancia_km: number
  coordenadas: {
    lat: number
    lon: number
  }
  data_cotacao: string
  hora_consulta: string
}

/**
 * AGC: Batch price response
 */
export interface BatchPriceResponse {
  precos: CropPrices
  referencia: PriceReference | null
  data_consulta: string
}

/**
 * AGC: Nearby plaza info
 */
export interface NearbyPlaza {
  id: string
  cidade: string
  estado: string
  pais: string
  fonte: string
  tipo: string
  distancia_km: number
  lat: number
  lon: number
  produtos_disponiveis: string[]
  cotacoes: { [key: string]: number }
}

// =============================================================================
// AGC: Default fallback prices (used when API unavailable)
// =============================================================================

const FALLBACK_PRICES: CropPrices = {
  soja: { preco: 125.0, unidade: 'R$/sc' },
  milho: { preco: 55.0, unidade: 'R$/sc' },
  milho_safrinha: { preco: 55.0, unidade: 'R$/sc' },
  trigo: { preco: 80.0, unidade: 'R$/sc' },
  cafe: { preco: 1150.0, unidade: 'R$/sc' },
  algodao: { preco: 125.0, unidade: 'R$/@' },
  arroz: { preco: 95.0, unidade: 'R$/sc' },
  feijao: { preco: 320.0, unidade: 'R$/sc' },
  sorgo: { preco: 45.0, unidade: 'R$/sc' },
}

/**
 * AGC: Normalize crop type to string
 * AGC: Handles various formats: string, object { id, name }, null/undefined
 * @param cropType - Crop type in any format
 * @returns Normalized lowercase string, or 'outros' for invalid/unknown
 */
const normalizeCropType = (cropType: unknown): string => {
  // AGC: Handle null/undefined
  if (!cropType) return 'outros'
  
  // AGC: Handle string
  if (typeof cropType === 'string') return cropType.toLowerCase()
  
  // AGC: Handle object with id or name property
  if (typeof cropType === 'object' && cropType !== null) {
    const obj = cropType as Record<string, unknown>
    if (typeof obj.id === 'string') return obj.id.toLowerCase()
    if (typeof obj.name === 'string') return obj.name.toLowerCase()
  }
  
  return 'outros'
}

/**
 * AGC: Normalize array of crop types to strings
 * AGC: Filters out 'outros' (invalid/unknown) entries for API requests
 * @param cropTypes - Array of crop types (any format)
 * @returns Array of normalized lowercase strings (excluding 'outros')
 */
const normalizeCropTypesArray = (cropTypes: unknown[]): string[] => {
  return cropTypes
    .map(normalizeCropType)
    .filter((s) => s.length > 0 && s !== 'outros')
}

// =============================================================================
// AGC: API Functions
// =============================================================================

/**
 * AGC: Get price for a single crop based on GPS location
 * @param cropType - Crop type (e.g., 'soja', 'milho')
 * @param lat - Latitude
 * @param lon - Longitude
 * @returns Price info or null
 */
export const getCropPrice = async (
  cropType: string,
  lat: number,
  lon: number
): Promise<SinglePriceResponse | null> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/prices/cotacao/${encodeURIComponent(cropType)}?lat=${lat}&lon=${lon}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (!response.ok) {
      console.warn(`⚠️ AGC: Price API returned ${response.status} for ${cropType}`)
      return null
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('❌ AGC: Error fetching crop price:', error)
    return null
  }
}

/**
 * AGC: Get prices for multiple crops at once (batch request)
 * AGC: This is the main function used by the dashboard
 * AGC: Handles crop types as strings or objects { id, name }
 * @param cropTypes - Array of crop types (strings or objects)
 * @param lat - Latitude
 * @param lon - Longitude
 * @param forceRefresh - Force refresh bypassing cache
 * @returns Batch price response
 */
export const getBatchPrices = async (
  cropTypes: unknown[],
  lat: number,
  lon: number,
  forceRefresh = false
): Promise<BatchPriceResponse> => {
  // AGC: Normalize crop types to strings (handles objects like { id, name })
  const normalizedCropTypes = normalizeCropTypesArray(cropTypes)
  
  // AGC: If no valid crop types, return empty response
  if (normalizedCropTypes.length === 0) {
    console.warn('⚠️ AGC: No valid crop types provided to getBatchPrices')
    return {
      precos: {},
      referencia: null,
      data_consulta: new Date().toISOString(),
    }
  }
  
  // AGC: Check cache first (unless forcing refresh or location changed)
  const cacheValid = priceCache && 
    Date.now() - priceCache.timestamp < CACHE_TTL &&
    isSameLocation(lat, lon, priceCache.lat, priceCache.lon)
    
  if (!forceRefresh && cacheValid) {
    console.log(`💰 AGC: Returning cached prices for ${priceCache.reference?.cidade || 'unknown'}`)
    return {
      precos: priceCache.data,
      referencia: priceCache.reference,
      data_consulta: new Date(priceCache.timestamp).toISOString(),
    }
  }

  try {
    const response = await fetch(`${BASE_URL}/api/v1/prices/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        lat,
        lon,
        produtos: normalizedCropTypes,
      }),
    })

    if (!response.ok) {
      throw new Error(`Price API returned ${response.status}`)
    }

    const data: BatchPriceResponse = await response.json()

    // AGC: Update cache with location info
    priceCache = {
      timestamp: Date.now(),
      lat,
      lon,
      data: data.precos,
      reference: data.referencia,
    }

    console.log(
      `💰 AGC: Fetched prices for ${Object.keys(data.precos).length} crops from ${data.referencia?.cidade || 'unknown'}`
    )

    return data
  } catch (error) {
    console.error('❌ AGC: Error fetching batch prices, using fallback:', error)

    // AGC: Return fallback prices
    const fallbackResponse: BatchPriceResponse = {
      precos: {},
      referencia: null,
      data_consulta: new Date().toISOString(),
    }

    // AGC: Build fallback prices for requested crops (already normalized)
    for (const crop of normalizedCropTypes) {
      if (FALLBACK_PRICES[crop]) {
        fallbackResponse.precos[crop] = FALLBACK_PRICES[crop]
      } else {
        // AGC: Default fallback for unknown crops
        fallbackResponse.precos[crop] = { preco: 100.0, unidade: 'R$/sc' }
      }
    }

    return fallbackResponse
  }
}

/**
 * AGC: Get prices using user's location (auto-detect GPS or use profile)
 * AGC: This is a convenience function for the dashboard
 * AGC: Handles crop types as strings or objects { id, name }
 * @param cropTypes - Array of crop types (strings or objects)
 * @param profileCity - User's city from profile (fallback)
 * @param profileState - User's state from profile (fallback)
 * @param forceRefresh - Force refresh bypassing cache
 */
export const getPricesForUserLocation = async (
  cropTypes: unknown[],
  profileCity?: string | null,
  profileState?: string | null,
  forceRefresh = false
): Promise<BatchPriceResponse & { locationSource: 'gps' | 'profile' | 'default' }> => {
  // AGC: Normalize crop types to strings (handles objects like { id, name })
  const normalizedCropTypes = normalizeCropTypesArray(cropTypes)
  
  try {
    // AGC: Get user's location (tries GPS first, then profile, then default)
    const location = await getCurrentLocation(profileCity, profileState)

    // AGC: Get batch prices for the location (already normalized)
    const prices = await getBatchPrices(normalizedCropTypes, location.lat, location.lon, forceRefresh)

    return {
      ...prices,
      locationSource: location.source,
    }
  } catch (error) {
    console.error('❌ AGC: Error getting prices for user location:', error)

    // AGC: Return fallback
    const fallbackResponse: BatchPriceResponse & { locationSource: 'gps' | 'profile' | 'default' } = {
      precos: {},
      referencia: null,
      data_consulta: new Date().toISOString(),
      locationSource: 'default',
    }

    // AGC: Build fallback with normalized crop types
    for (const crop of normalizedCropTypes) {
      if (FALLBACK_PRICES[crop]) {
        fallbackResponse.precos[crop] = FALLBACK_PRICES[crop]
      } else {
        fallbackResponse.precos[crop] = { preco: 100.0, unidade: 'R$/sc' }
      }
    }

    return fallbackResponse
  }
}

/**
 * AGC: Get nearby pricing plazas
 * @param lat - Latitude
 * @param lon - Longitude
 * @param raioKm - Search radius in km (default 300)
 */
export const getNearbyPlazas = async (
  lat: number,
  lon: number,
  raioKm = 300
): Promise<NearbyPlaza[]> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/prices/cercanas?lat=${lat}&lon=${lon}&raio=${raioKm}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (!response.ok) {
      return []
    }

    const data = await response.json()
    return data.pracas || []
  } catch (error) {
    console.error('❌ AGC: Error fetching nearby plazas:', error)
    return []
  }
}

/**
 * AGC: Clear price cache
 * AGC: Call this when user wants to force refresh
 */
export const clearPriceCache = (): void => {
  priceCache = null
  console.log('🗑️ AGC: Price cache cleared')
}

/**
 * AGC: Trigger scraper to fetch new prices from AGROLINK and save to Firestore for all users
 * AGC: Requires authenticated user (Bearer token). Updates Firebase commodity prices globally.
 * @param token - Auth token (e.g. from getAuthToken())
 * @returns { success: true } or { success: false, error: string }
 */
export const triggerScrapePrices = async (token: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(`${BASE_URL}/api/v1/prices/admin/scrape`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      return { success: false, error: data.error || data.message || `HTTP ${response.status}` }
    }
    if (!data.success) {
      return { success: false, error: data.error || data.data?.error || 'Scraper failed' }
    }
    return { success: true }
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error'
    return { success: false, error: message }
  }
}

/**
 * AGC: Get price for a crop type (with fallback)
 * AGC: Returns the price number or fallback value
 * @param cropType - Crop type
 * @param prices - Prices object from batch response
 * @param fallback - Fallback price if not found
 */
export const getPriceForCrop = (
  cropType: unknown,
  prices: CropPrices,
  fallback = 100
): number => {
  const normalizedCrop = normalizeCropType(cropType)
  const originalCrop = typeof cropType === 'string' ? cropType : normalizedCrop

  // AGC: Try exact match first
  if (prices[originalCrop]) {
    return prices[originalCrop].preco
  }

  // AGC: Try normalized match
  if (prices[normalizedCrop]) {
    return prices[normalizedCrop].preco
  }

  // AGC: Handle milho_safrinha -> milho
  if (normalizedCrop === 'milho_safrinha' && prices['milho']) {
    return prices['milho'].preco
  }

  // AGC: Try fallback prices
  if (FALLBACK_PRICES[normalizedCrop]) {
    return FALLBACK_PRICES[normalizedCrop].preco
  }

  return fallback
}

/**
 * AGC: Get unit for a crop type
 * @param cropType - Crop type
 * @param prices - Prices object from batch response
 */
export const getUnitForCrop = (cropType: unknown, prices: CropPrices): string => {
  const normalizedCrop = normalizeCropType(cropType)
  const originalCrop = typeof cropType === 'string' ? cropType : normalizedCrop

  if (prices[originalCrop]) {
    return prices[originalCrop].unidade
  }

  if (prices[normalizedCrop]) {
    return prices[normalizedCrop].unidade
  }

  // AGC: Default to saca for Brazilian crops
  return 'R$/sc'
}

// =============================================================================
// AGC: Yield data (default projections by crop type)
// AGC: These can be overridden by lot-specific data
// =============================================================================

export const DEFAULT_YIELDS: { [key: string]: number } = {
  soja: 60, // AGC: sacas/ha
  milho: 150, // AGC: sacas/ha
  milho_safrinha: 100, // AGC: sacas/ha
  trigo: 45, // AGC: sacas/ha
  cafe: 35, // AGC: sacas/ha
  algodao: 280, // AGC: @/ha (arrobas)
  arroz: 130, // AGC: sacas/ha
  feijao: 30, // AGC: sacas/ha
  sorgo: 70, // AGC: sacas/ha
  cana: 85, // AGC: tons/ha (special case)
}

/**
 * AGC: Get default yield for a crop type
 * @param cropType - Crop type (string or object)
 * @returns Yield in sacas/ha (or tons/ha for cana)
 */
export const getDefaultYield = (cropType: unknown): number => {
  const normalizedCrop = normalizeCropType(cropType)
  return DEFAULT_YIELDS[normalizedCrop] || 50
}

// =============================================================================
// AGC: Cost data (default costs per hectare by crop type)
// =============================================================================

export const DEFAULT_COSTS_PER_HA: { [key: string]: number } = {
  soja: 4500, // AGC: R$/ha
  milho: 5200, // AGC: R$/ha
  milho_safrinha: 3800, // AGC: R$/ha
  trigo: 3800, // AGC: R$/ha
  cafe: 8500, // AGC: R$/ha
  algodao: 12000, // AGC: R$/ha
  arroz: 6000, // AGC: R$/ha
  feijao: 4000, // AGC: R$/ha
  sorgo: 3000, // AGC: R$/ha
  cana: 6500, // AGC: R$/ha
}

/**
 * AGC: Get default cost per hectare for a crop type
 * @param cropType - Crop type (string or object)
 * @returns Cost in R$/ha
 */
export const getDefaultCostPerHa = (cropType: unknown): number => {
  const normalizedCrop = normalizeCropType(cropType)
  return DEFAULT_COSTS_PER_HA[normalizedCrop] || 4000
}

export default {
  getCropPrice,
  getBatchPrices,
  getPricesForUserLocation,
  getNearbyPlazas,
  clearPriceCache,
  getPriceForCrop,
  getUnitForCrop,
  getDefaultYield,
  getDefaultCostPerHa,
  DEFAULT_YIELDS,
  DEFAULT_COSTS_PER_HA,
  FALLBACK_PRICES,
}

